import { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { axios } from "../../axios";
import { useAtomValue, useSetAtom } from "jotai";
import { authAtom } from "../../auth/auth.atom";
import { UserInterface } from "../../interfaces/user.interface";
import { User } from "../../models/User";
import toast from "react-hot-toast";
import { wait } from "../../utils/wait";
import { Button, Form, Input, Modal } from "antd";
import { useNavigate } from "react-router";
import { AuthForm } from "../../components/auth-form/auth-form.component";
import { showMessage } from "../../utils/showMessage";
import { AxiosError, AxiosResponse } from "axios";
import { redactText } from "../../utils/redactText";
import FormItemLabel from "antd/lib/form/FormItemLabel";
import { useSearchParams } from "react-router-dom";
import { useNavigateToRedirect } from "../../utils/navigateToRedirect";
import { needsVerification } from "../../utils/verification";

export const VerifyPage = () => {
  const [searchParams] = useSearchParams();

  const [form] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const auth = useAtomValue(authAtom);
  const allowMobileOTPSend = useRef(true);
  const allowEmailOTPSend = useRef(true);
  const autoSentMobileForUser = useRef<string | null>(null);
  const autoSentEmailForUser = useRef<string | null>(null);

  const setAuth = useSetAtom(authAtom);
  const routerNavigate = useNavigate();
  const navigateToRedirect = useNavigateToRedirect();

  function sendOTP(channel: "SMS" | "WHATSAPP") {
    if (auth.user?.verifiedMobile) {
      toast.success("Your Mobile OTP is verified, leave the field blank");
      return;
    }
    if (!auth.authenticated || !allowMobileOTPSend.current) return;

    allowMobileOTPSend.current = false;
    const sendOtpDto = {
      channel,
      phone: {
        code: auth.user?.mobile?.code,
        number: auth.user?.mobile?.phone,
      },
    };

    axios
      .post("/auth/otp/send/phone", sendOtpDto)
      .then((result) => {
        allowMobileOTPSend.current = true;
        showMessage(result);
      })
      .catch((e) => {
        allowMobileOTPSend.current = true;
        toast.error(e.response?.data?.error ?? e.response?.data?.message ?? "Failed to send phone OTP");
      });
  }

  function sendEmailOTP() {
    if (auth.user?.verifiedEmail) {
      toast.success("Your Email OTP is verified, leave the field blank");
      return;
    }
    if (!auth.authenticated || !allowEmailOTPSend.current) return;

    allowEmailOTPSend.current = false;
    const sendOtpDto = {
      email: auth.user?.email,
    };

    axios
      .post("/auth/otp/send/email", sendOtpDto)
      .then((result) => {
        allowEmailOTPSend.current = true;
        showMessage(result);
      })
      .catch((e) => {
        allowEmailOTPSend.current = true;
        toast.error("Error Sending Email OTP");
      });
  }

  const onSubmit = useCallback(
    async ({
      otpMobile,
      otpEmail,
    }: {
      otpMobile: string;
      otpEmail: string;
    }) => {
      try {
        const trimmedMobileOtp = (otpMobile ?? "").trim();
        const trimmedEmailOtp = (otpEmail ?? "").trim();

        let phoneResult: AxiosResponse<{
          user: UserInterface;
          tmp_token: string;
        }> | null = null;
        let emailResult: AxiosResponse<{
          user: UserInterface;
          tmp_token: string;
        }> | null = null;
        let attemptedPhoneVerify = false;
        let attemptedEmailVerify = false;

        // Only verify phone if a phone exists AND it's not verified AND a mobile OTP was entered
        if (
          auth.user?.mobile?.code &&
          auth.user?.mobile?.phone &&
          !auth.user?.verifiedMobile &&
          trimmedMobileOtp.length > 0
        ) {
          attemptedPhoneVerify = true;
          const verifyPhoneOtpDto = {
            phone: {
              code: auth.user?.mobile?.code,
              number: auth.user?.mobile?.phone,
            },
            otp: trimmedMobileOtp,
          };
          phoneResult = await axios.post<{
            user: UserInterface;
            tmp_token: string;
          }>("/auth/otp/verify/phone", verifyPhoneOtpDto);
        }
        // Only verify email if an email exists AND it's not verified AND an email OTP was entered
        if (
          auth.user?.email &&
          !auth.user?.verifiedEmail &&
          trimmedEmailOtp.length > 0
        ) {
          attemptedEmailVerify = true;
          const verifyEmailOtpDto = {
            email: auth.user?.email,
            otp: trimmedEmailOtp,
          };
          emailResult = await axios.post<{
            user: UserInterface;
            tmp_token: string;
          }>("/auth/otp/verify/email", verifyEmailOtpDto);
        }
        // If nothing was attempted, prompt the user
        if (!attemptedPhoneVerify && !attemptedEmailVerify) {
          toast.error("Please enter at least one OTP to verify.");
          return;
        }

        // Success conditions
        const phoneOk = phoneResult?.status === 200;
        const emailOk = emailResult?.status === 200;

        if ((attemptedPhoneVerify ? phoneOk : true) && (attemptedEmailVerify ? emailOk : true)) {
          toast.success("Verified! Redirecting...");
          sessionStorage.setItem(
            "tmp_token",
            phoneResult?.data.tmp_token || emailResult?.data.tmp_token || ""
          );

          // Pull authoritative user state from the server so the atom reflects
          // what the DB actually has (verifiedEmail / verifiedMobile flags),
          // not whatever was serialized mid-flight by either OTP endpoint.
          try {
            const me = await axios.get<{ user: UserInterface }>("/auth/user/me");
            setAuth({
              authenticated: true,
              user: User.fromJson(me.data.user ?? (me.data as any)),
              loaded: true,
            });
          } catch {
            const verifiedUserPayload =
              emailResult?.data.user ?? phoneResult?.data.user;
            setAuth({
              authenticated: true,
              user: verifiedUserPayload
                ? User.fromJson(verifiedUserPayload)
                : auth.user,
              loaded: true,
            });
          }

          await wait(500);
          navigateToRedirect();
          return;
        }

        // Error messaging based on what was attempted
        if (attemptedPhoneVerify && attemptedEmailVerify) {
          if (!phoneOk && !emailOk) {
            toast.error("Both OTPs are incorrect!");
          } else if (!phoneOk) {
            toast.error("Mobile OTP is incorrect");
          } else if (!emailOk) {
            toast.error("Email OTP is incorrect!");
          }
        } else if (attemptedPhoneVerify) {
          toast.error("Mobile OTP is incorrect");
        } else if (attemptedEmailVerify) {
          toast.error("Email OTP is incorrect!");
        }
      } catch (e) {
        showMessage(e as AxiosError, "error");
      }
    },
    [searchParams, auth]
  );

  useEffect(() => {
    // Leave as soon as nothing is actually pending. A phone-only account has no
    // address to confirm, so waiting on `verifiedEmail` would strand it on a
    // form with no fields.
    if (auth.authenticated && !needsVerification(auth.user)) {
      navigateToRedirect();
    }
  }, [auth, searchParams]);

  useEffect(() => {
    if (!auth.authenticated || !auth.user) return;
    const userId = auth.user.id;

    if (
      !auth.user.verifiedMobile &&
      auth.user.mobile?.code &&
      auth.user.mobile?.phone &&
      autoSentMobileForUser.current !== userId
    ) {
      autoSentMobileForUser.current = userId;
      sendOTP("SMS");
    }

    if (
      !auth.user.verifiedEmail &&
      auth.user.email &&
      autoSentEmailForUser.current !== userId
    ) {
      autoSentEmailForUser.current = userId;
      sendEmailOTP();
    }
  }, [auth.authenticated, auth.user?.id, auth.user?.verifiedMobile, auth.user?.verifiedEmail]);

  return (
    <AuthForm form={form} onSubmit={onSubmit}>
      {contextHolder}
      <MobileCol style={{ width: "100%" }}>
        {!auth.user?.verifiedMobile && auth.user?.mobile?.code && auth.user?.mobile?.phone ? (
          <>
            <SentToText>{`Sent to +${auth.user?.mobile?.code}${redactText(
              auth.user?.mobile?.phone ?? "",
              4
            )}`}</SentToText>
            <Form.Item
              name="otpMobile"
              rules={[
                {
                  type: "string",
                  required: !auth.user?.verifiedMobile,
                  message: "Please input your otp!",
                },
                {
                  pattern: /[0-9]+$/,
                  message: "OTP can only have numeric characters!",
                },
                {
                  validator: (_, value) => {
                    if (value && value.length > 6) {
                      return Promise.reject(
                        new Error(
                          "OTP can have a maximum length of 6 characters!"
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                size="large"
                id="mobile-otp"
                placeholder="Mobile OTP"
                addonAfter={
                  <Button
                    type="primary"
                    onClick={(event) => {
                      modal.confirm({
                        title: "Resend OTP via",
                        okText: "SMS",
                        cancelText: "WhatsApp",
                        onCancel: () => {
                          sendOTP("WHATSAPP");
                        },
                        onOk: () => {
                          sendOTP("SMS");
                        },
                      });
                    }}
                  >
                    Resend
                  </Button>
                }
              />
            </Form.Item>
          </>
        ) : null}
        {!auth.user?.verifiedEmail && auth.user?.email ? (
          <>
            <SentToText>{`Sent to ${redactText(auth.user?.email ?? "", 5, {
              before: "@",
            })}`}</SentToText>
            <Form.Item
              name="otpEmail"
              rules={[
                {
                  type: "string",
                  required: !auth.user?.verifiedEmail,
                  message: "Please input your OTP!",
                },
                {
                  len: 6,
                  message: "OTP should be 6 digits long!",
                },
                {
                  pattern: /[0-9]+$/,
                  message: "OTP can only have numeric characters!",
                },
              ]}
            >
              <Input
                size="large"
                id="email-otp"
                placeholder="Email OTP"
                addonAfter={
                  <Button
                    type="primary"
                    onClick={(event) => {
                      sendEmailOTP();
                    }}
                  >
                    Resend
                  </Button>
                }
              />
            </Form.Item>
          </>
        ) : null}
      </MobileCol>
      <Form.Item style={{ width: "100%" }}>
        <Button
          block
          type="primary"
          size="large"
          shape="round"
          htmlType="submit"
          style={{ width: "100%" }}
        >
          Continue
        </Button>
      </Form.Item>
      <RegisterText>
        <p
          onClick={async () => {
            try {
              const result = await axios.post("/auth/logout");
              showMessage(result);
              // The verify guard sends an authenticated user straight back here,
              // so the local session has to be cleared before the route changes.
              sessionStorage.removeItem("tmp_token");
              setAuth({
                authenticated: false,
                user: null,
                loaded: true,
              });
              routerNavigate(
                { pathname: "/login", search: window.location.search },
                { replace: true }
              );
            } catch (e) {
              showMessage(e as AxiosError, "error");
            }
          }}
        >
          Logout
        </p>
      </RegisterText>
    </AuthForm>
  );
};

const MobileCol = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RegisterText = styled.h3`
  font-size: 1.6rem;
  color: #fff;
  font-weight: 500;
  cursor: pointer;
  text-align: center;

  & text {
    color: #2268bd;
    cursor: pointer;
  }
`;

const SentToText = styled.span`
  color: #fff;
  opacity: 0.75;
  font-size: 1.4rem;
  font-weight: 500;
`;
